import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina clases de Tailwind con deduplicación inteligente.
 * Patrón estándar shadcn/ui — úsalo en todos los componentes.
 *
 * @example
 *   <div className={cn('p-4 bg-white', isActive && 'bg-blue-50', className)} />
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formatea un número como entero local (separadores de miles).
 * Pensado para KPIs y conteos del dashboard.
 */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}

/**
 * Devuelve una fecha legible relativa al ahora ("hace 2 días", "en 5 días").
 * Si la diferencia supera 60 días, fallback a fecha absoluta.
 */
export function formatRelativeDate(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = date.getTime() - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(diffDays) > 60) {
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  const rtf = new Intl.RelativeTimeFormat('es-CO', { numeric: 'auto' });
  return rtf.format(diffDays, 'day');
}

/** Iniciales para Avatar fallback. */
export function initials(name: string | undefined | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('');
}
